/**
 * Positioning utility — computes node layout for the adjacency map canvas.
 *
 * 3-pass algorithm with full-depth radial distribution:
 *   Pass 1 — Build parent-child lineage chains within each axis
 *   Pass 2 — Assign angular slots (largest chains get center positions)
 *   Pass 3 — Position nodes via claimRadialSlot() for unique radii per node
 *
 * Every node at a given (axis, ring) gets a unique radial position spanning
 * the full band depth. Chain roots claim inner slots, children get outer.
 *
 * All positions are relative to canvas center (0, 0).
 */

import type {
  AdjacencyNode,
  AdjacencyAxis,
  RelatednessRing,
} from '../types/adjacency';
import { AXIS_ANGLES } from '../types/adjacency';

// ── Types ────────────────────────────────────────────────────────────

export interface CanvasSize {
  width: number;
  height: number;
}

export interface Position {
  x: number;
  y: number;
}

export interface LabelPosition extends Position {
  anchor: 'start' | 'middle' | 'end';
}

interface LineageChain {
  rootId: string;
  nodesByRing: Map<RelatednessRing, AdjacencyNode[]>;
  totalNodes: number;
}

// ── Constants ────────────────────────────────────────────────────────

/**
 * Core offset — fraction of canvasRadius where rings begin.
 * Core circle is 400 SVG units; canvasRadius is 5000 → 0.08.
 * Add a small gap so ring 1 doesn't touch the core circle.
 */
const CORE_OFFSET = 0.12;

/**
 * Ring band boundaries (fraction of canvasRadius).
 * Equal-width bands give each ring ~1050 SVG units of radial depth
 * (at canvasRadius=5000). Ring 3 fits 3×3 grid of 625×228 cards
 * with generous spacing for relationship lines.
 *
 * Ring lines sit at each band's center.
 */
export const RING_BANDS: Record<RelatednessRing, { inner: number; outer: number }> = {
  0: { inner: 0.00, outer: 0.12 },
  1: { inner: 0.12, outer: 0.33 },
  2: { inner: 0.33, outer: 0.54 },
  3: { inner: 0.54, outer: 0.75 },
  4: { inner: 0.75, outer: 0.95 },
};

/**
 * Visual ring line positions (fraction of canvasRadius).
 * Centered within each band for even visual distribution.
 * Used ONLY for drawing reference circles — nodes do NOT snap to these.
 */
const RING_LINE_POSITIONS: Record<RelatednessRing, number> = {
  0: 0,
  1: 0.225,
  2: 0.435,
  3: 0.645,
  4: 0.85,
};

/**
 * Logical canvas dimensions (SVG coordinate space).
 * Much larger than viewport — pan/zoom handles navigation.
 * Larger space = more room between nodes = less overlap.
 */
export const LOGICAL_CANVAS_SIZE = 10000;

/**
 * Core circle radius in SVG units (logical canvas space).
 * Sized to contain 6 card-mode nodes (260×62 each) on the inner ring.
 * Node outer edge at ring + half-card-width = 250 + 130 = 380 < 400.
 */
export const CORE_CIRCLE_RADIUS = 400;

/**
 * Radius of the ring on which core nodes are placed (SVG units).
 * For 6 equally-spaced nodes, adjacent center distance = R.
 * Card width is 260, so R=250 gives near-touching cards at full zoom.
 */
const CORE_NODE_RING_RADIUS = 250;

/** Max angular spread per slot within a sector (degrees). */
const MAX_SPREAD_PER_NODE = 5;

/** Half-sector width (degrees). 360° / 16 = 22.5°. */
const HALF_SECTOR = 22.5;

// ── Helpers ──────────────────────────────────────────────────────────

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Convert CW-from-top degrees to SVG radians.
 * 0° CW from top → -90° math → points up (negative y).
 */
function cwTopToSvgRad(cwDeg: number): number {
  return degToRad(cwDeg - 90);
}

/** Group array items by a key function. */
function groupBy<T, K>(items: T[], keyFn: (item: T) => K): Map<K, T[]> {
  const map = new Map<K, T[]>();
  for (const item of items) {
    const key = keyFn(item);
    const group = map.get(key);
    if (group) {
      group.push(item);
    } else {
      map.set(key, [item]);
    }
  }
  return map;
}

/**
 * Reorder array so center element comes first, then alternating left/right:
 * [mid, mid-1, mid+1, mid-2, mid+2, ...].
 * Largest chains get the prime center angular positions.
 */
function centerOutOrder<T>(arr: T[]): T[] {
  if (arr.length <= 1) return [...arr];
  const result: T[] = [];
  const mid = Math.floor(arr.length / 2);
  result.push(arr[mid]);
  for (let offset = 1; offset <= mid; offset++) {
    if (mid - offset >= 0) result.push(arr[mid - offset]);
    if (mid + offset < arr.length) result.push(arr[mid + offset]);
  }
  return result;
}

// ── Ring Radius ──────────────────────────────────────────────────────

/**
 * Get the SVG radius for a given ring's visual reference line.
 * Used for drawing ring circles — NOT for node placement.
 */
export function getRingRadius(ring: RelatednessRing, canvasRadius: number): number {
  return RING_LINE_POSITIONS[ring] * canvasRadius;
}

// ── Axis Helpers ─────────────────────────────────────────────────────

export function getAxisEndpoint(axis: AdjacencyAxis, canvasRadius: number): Position {
  const angle = AXIS_ANGLES[axis];
  if (angle < 0) return { x: 0, y: 0 };

  const outerRadius = canvasRadius * 0.98;
  const rad = cwTopToSvgRad(angle);
  return {
    x: outerRadius * Math.cos(rad),
    y: outerRadius * Math.sin(rad),
  };
}

export function getAxisLabelPosition(
  axis: AdjacencyAxis,
  canvasRadius: number,
): LabelPosition {
  const angle = AXIS_ANGLES[axis];
  if (angle < 0) return { x: 0, y: 0, anchor: 'middle' };

  const labelRadius = canvasRadius * 1.02;
  const rad = cwTopToSvgRad(angle);
  const x = labelRadius * Math.cos(rad);
  const y = labelRadius * Math.sin(rad);

  let anchor: LabelPosition['anchor'];
  const threshold = labelRadius * 0.1;
  if (Math.abs(x) < threshold) {
    anchor = 'middle';
  } else if (x > 0) {
    anchor = 'start';
  } else {
    anchor = 'end';
  }

  return { x, y, anchor };
}

// ── Node Positioning — 3-Pass Algorithm ──────────────────────────────

/**
 * Compute positions for ALL nodes using lineage chains and full-depth
 * radial distribution within ring bands.
 *
 * **Pass 1** — Build parent-child lineage chains within each axis.
 * **Pass 2** — Assign angular slots (largest chains get center positions).
 * **Pass 3** — Position via claimRadialSlot() — every node gets a unique radius.
 */
export function computeAllPositions(
  nodes: AdjacencyNode[],
  canvasSize: CanvasSize,
): Map<string, Position> {
  const positions = new Map<string, Position>();
  const canvasRadius = Math.min(canvasSize.width, canvasSize.height) / 2;

  // Handle manual overrides and core nodes first
  // mapPosition stores normalized coordinates (fraction of canvasRadius, centered at 0,0)
  // Radial remapping pushes positions outward past the core circle.
  const remapScale = 0.95 - CORE_OFFSET;
  const coreNodes: AdjacencyNode[] = [];
  const remainingNodes: AdjacencyNode[] = [];
  for (const node of nodes) {
    if (node.mapPosition) {
      const mx = node.mapPosition.x;
      const my = node.mapPosition.y;
      const rawR = Math.sqrt(mx * mx + my * my);
      if (Math.abs(mx) < 0.0001 && Math.abs(my) < 0.0001) {
        // At center — place at origin (core node with override)
        positions.set(node.id, { x: 0, y: 0 });
      } else {
        // Remap radius outward past core circle
        const angle = Math.atan2(my, mx);
        const newR = CORE_OFFSET + rawR * remapScale;
        positions.set(node.id, {
          x: newR * Math.cos(angle) * canvasRadius,
          y: newR * Math.sin(angle) * canvasRadius,
        });
      }
    } else if (node.adjacencyAxis === 'Core' || node.relatednessRing === 0) {
      coreNodes.push(node);
    } else {
      remainingNodes.push(node);
    }
  }

  // Place core nodes in a circle around center
  if (coreNodes.length === 1) {
    positions.set(coreNodes[0].id, { x: 0, y: 0 });
  } else {
    const angleStep = (2 * Math.PI) / coreNodes.length;
    for (let i = 0; i < coreNodes.length; i++) {
      // Start from top (-π/2) and go clockwise
      const angle = -Math.PI / 2 + i * angleStep;
      positions.set(coreNodes[i].id, {
        x: CORE_NODE_RING_RADIUS * Math.cos(angle),
        y: CORE_NODE_RING_RADIUS * Math.sin(angle),
      });
    }
  }

  // Group by axis
  const byAxis = groupBy(remainingNodes, (n) => n.adjacencyAxis);

  for (const [axis, axisNodes] of byAxis) {
    const centerAngle = AXIS_ANGLES[axis];
    if (centerAngle === undefined || centerAngle < 0) continue;

    const axisNodeIds = new Set(axisNodes.map((n) => n.id));

    // ═══════════════════════════════════════════
    // PASS 1: Build lineage chains
    // ═══════════════════════════════════════════

    const roots = axisNodes.filter(
      (n) =>
        n.parentIds.length === 0 ||
        n.parentIds.every((pid) => !axisNodeIds.has(pid)),
    );

    const chains: LineageChain[] = [];
    const assignedToChain = new Set<string>();

    for (const root of roots) {
      const chain: LineageChain = {
        rootId: root.id,
        nodesByRing: new Map(),
        totalNodes: 0,
      };

      const queue = [root];
      while (queue.length > 0) {
        const current = queue.shift()!;
        if (assignedToChain.has(current.id)) continue;
        assignedToChain.add(current.id);

        const ringNodes = chain.nodesByRing.get(current.relatednessRing) ?? [];
        ringNodes.push(current);
        chain.nodesByRing.set(current.relatednessRing, ringNodes);
        chain.totalNodes++;

        // Find children on this axis
        const children = axisNodes.filter(
          (n) =>
            n.parentIds.includes(current.id) && !assignedToChain.has(n.id),
        );
        queue.push(...children);
      }

      if (chain.totalNodes > 0) chains.push(chain);
    }

    const orphans = axisNodes.filter((n) => !assignedToChain.has(n.id));

    // ═══════════════════════════════════════════
    // PASS 2: Assign angular slots
    // ═══════════════════════════════════════════

    chains.sort((a, b) => b.totalNodes - a.totalNodes);

    const totalSlots = chains.length + Math.ceil(orphans.length / 3);
    const slotSpacing = Math.min(
      MAX_SPREAD_PER_NODE,
      (HALF_SECTOR * 2) / Math.max(totalSlots, 1),
    );
    const totalSpread = slotSpacing * (totalSlots - 1);
    const startAngle = centerAngle - totalSpread / 2;

    const slotAngles: number[] = [];
    for (let i = 0; i < totalSlots; i++) {
      slotAngles.push(startAngle + i * slotSpacing);
    }
    const centerOutSlots = centerOutOrder(slotAngles);

    // ═══════════════════════════════════════════
    // PRE-PASS 3: Allocate radial slots per ring
    // ═══════════════════════════════════════════

    // Count total nodes per ring on this axis
    const ringNodeCounts = new Map<RelatednessRing, number>();
    const ringSlotCounters = new Map<RelatednessRing, number>();

    for (const node of axisNodes) {
      const count = ringNodeCounts.get(node.relatednessRing) ?? 0;
      ringNodeCounts.set(node.relatednessRing, count + 1);
      ringSlotCounters.set(node.relatednessRing, 0);
    }

    /**
     * Claim the next unique radial position within a ring's band.
     * Distributes evenly from inner+padding to outer-padding.
     * Chain roots claim first (inner), children claim next (outer).
     */
    function claimRadialSlot(ring: RelatednessRing): number {
      const totalSlots = ringNodeCounts.get(ring) ?? 1;
      const currentSlot = ringSlotCounters.get(ring) ?? 0;
      ringSlotCounters.set(ring, currentSlot + 1);

      const { inner, outer } = RING_BANDS[ring];
      if (totalSlots === 1) return ((inner + outer) / 2) * canvasRadius;

      const padding = (outer - inner) * 0.08;
      const usableInner = inner + padding;
      const usableOuter = outer - padding;
      const fraction = currentSlot / (totalSlots - 1);
      return (usableInner + fraction * (usableOuter - usableInner)) * canvasRadius;
    }

    // ═══════════════════════════════════════════
    // PASS 3: Position nodes
    // ═══════════════════════════════════════════

    // 3a: Chain nodes — process rings ascending so roots get inner slots
    let slotIdx = 0;

    for (const chain of chains) {
      const chainAngle = centerOutSlots[slotIdx++] ?? centerAngle;

      // Sort rings ascending: root ring first → inner radial slots
      const sortedRings = [...chain.nodesByRing.entries()]
        .sort(([a], [b]) => a - b);

      for (const [ring, ringNodes] of sortedRings) {
        if (ringNodes.length === 1) {
          const radius = claimRadialSlot(ring);
          const rad = cwTopToSvgRad(chainAngle);
          positions.set(ringNodes[0].id, {
            x: radius * Math.cos(rad),
            y: radius * Math.sin(rad),
          });
        } else {
          // Multiple siblings: fan angularly + each gets distinct radial slot
          const sibSpread = slotSpacing * 0.6;
          const sibStart = chainAngle - (sibSpread * (ringNodes.length - 1)) / 2;
          ringNodes.sort((a, b) => a.name.localeCompare(b.name));

          for (let i = 0; i < ringNodes.length; i++) {
            const angle = sibStart + i * sibSpread;
            const radius = claimRadialSlot(ring);
            const rad = cwTopToSvgRad(angle);
            positions.set(ringNodes[i].id, {
              x: radius * Math.cos(rad),
              y: radius * Math.sin(rad),
            });
          }
        }
      }
    }

    // 3b: Orphans — fill remaining angular + radial slots
    if (orphans.length > 0) {
      const orphansByRing = groupBy(orphans, (n) => n.relatednessRing);
      const remainingAngles = centerOutSlots.slice(slotIdx);

      const orphanAngles =
        remainingAngles.length > 0
          ? remainingAngles
          : (() => {
              const count = Math.ceil(orphans.length / 4);
              const spacing = (HALF_SECTOR * 2) / (count + 1);
              return Array.from({ length: count }, (_, i) =>
                centerAngle - HALF_SECTOR + (i + 1) * spacing,
              );
            })();

      let angleIdx = 0;

      for (const [ring, ringOrphans] of orphansByRing) {
        ringOrphans.sort((a, b) => a.name.localeCompare(b.name));
        for (const node of ringOrphans) {
          const angle = orphanAngles[angleIdx % orphanAngles.length];
          angleIdx++;
          const radius = claimRadialSlot(ring);
          const rad = cwTopToSvgRad(angle);
          positions.set(node.id, {
            x: radius * Math.cos(rad),
            y: radius * Math.sin(rad),
          });
        }
      }
    }
  }

  return positions;
}
